import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'antd';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { toast } from 'react-hot-toast';

import { OrderDetailView } from '../components/OrderDetailView';
import { OrderStatusModal } from '../components/modals/OrderStatusModal';
import { EvolutionJobNumberModal } from '../components/modals/EvolutionJobNumberModal';
import { ResendPaymentLinkModal } from '../components/modals/ResendPaymentLinkModal';
import { OrderStatus, OrderStateCode } from '../constants';
import { useOrderDetail, useUpdateOrder, useFulfillSalesOrder, useCancelSalesOrder } from '../hooks';
import { createPaymentApi } from '../api/dataverse';
import { getOrderNumber } from '../utils';

export const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: order } = useOrderDetail(id as string);

  const [orderStatusModalOpen, setOrderStatusModalOpen] = useState(false);
  const [evolutionJobModalOpen, setEvolutionJobModalOpen] = useState(false);
const [resendPaymentModalOpen, setResendPaymentModalOpen] = useState(false);
  const [isSendingPayment, setIsSendingPayment] = useState(false);

  const updateOrderMutation = useUpdateOrder();
  const fulfillSalesOrderMutation = useFulfillSalesOrder();
  const cancelSalesOrderMutation = useCancelSalesOrder();

  const handleUpdateOrderStatus = (orderId: string, orderStatus: OrderStatus, evolutionJobNumber?: string) => {
    if (orderStatus === OrderStatus.Completed) {
      fulfillSalesOrderMutation.mutate(
        {
          OrderClose: {
            subject: 'Order Fulfilled',
            'salesorderid@odata.bind': `/salesorders(${orderId})`,
          },
          Status: 100001,
        },
        { onSuccess: () => setOrderStatusModalOpen(false) }
      );
      return;
    }

    if (orderStatus === OrderStatus.Cancelled) {
      cancelSalesOrderMutation.mutate(
        {
          OrderClose: {
            '@odata.type': '#Microsoft.Dynamics.CRM.orderclose',
            actualend: new Date().toISOString(),
            description: null,
            salesorderid: orderId,
            'salesorderid@odata.bind': `/salesorders(${orderId})`,
          },
          Status: 4,
        } as any,
        { onSuccess: () => setOrderStatusModalOpen(false) }
      );
      return;
    }

    let payload: any = { id: orderId };
    if (orderStatus === OrderStatus.IN_PRODUCTION) {
      payload = { ...payload, statecode: OrderStateCode.Active, statuscode: 736000001, dogma_evolutionjobnumber: evolutionJobNumber };
    }
    updateOrderMutation.mutate(payload, {
      onSuccess: () => setOrderStatusModalOpen(false),
    });
  };

  const handleSaveEvolutionJobNumber = (orderId: string, evolutionJobNumber: string) => {
    updateOrderMutation.mutate(
      { id: orderId, dogma_evolutionjobnumber: evolutionJobNumber },
      { onSuccess: () => setEvolutionJobModalOpen(false) }
    );
  };

const handleResendPaymentLink = async (destination: 'email' | 'sms') => {
    if (!order) return;
    setIsSendingPayment(true);
    try {
      const exchangeRate = order.quoteid?.dogma_exchangerate ?? order.quoteid?.dogma_DealersCurrency?.dogma_exchangerate ?? 1;
      const vssTotal = order.dogma_vsstotalamount ?? 0;
      await createPaymentApi({
        total: Math.round(vssTotal * exchangeRate * 100),
        currency: order.quoteid?.dogma_DealersCurrency?.dogma_isocurrencycode ?? 'GBP',
        userPaymentId: `${getOrderNumber(order)}-${order['_customerid_value@OData.Community.Display.V1.FormattedValue'] ?? ''}`,
        email: order.customerid_contact?.emailaddress1 ?? '',
        phone: order.customerid_contact?.telephone1 ?? '',
        orderid: order.salesorderid,
        destination,
      });
      toast.success('Payment link sent successfully');
      setResendPaymentModalOpen(false);
    } catch {
      toast.error('Failed to send payment link');
    } finally {
      setIsSendingPayment(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 w-full">
      <Button
        type="text"
        icon={<IoMdArrowRoundBack />}
        onClick={() => navigate(-1)}
        className="mb-4"
      >
        Back to Orders
      </Button>

      <OrderDetailView
        orderId={id as string}
        onChangeOrderStatus={() => setOrderStatusModalOpen(true)}
onResendPaymentLink={() => setResendPaymentModalOpen(true)}
        onEditEvolutionJobNumber={() => setEvolutionJobModalOpen(true)}
      />

      <OrderStatusModal
        open={orderStatusModalOpen}
        order={order ?? null}
        onCancel={() => setOrderStatusModalOpen(false)}
        onConfirm={handleUpdateOrderStatus}
        loading={
          updateOrderMutation.isPending ||
          fulfillSalesOrderMutation.isPending ||
          cancelSalesOrderMutation.isPending
        }
      />

      <EvolutionJobNumberModal
        open={evolutionJobModalOpen}
        order={order ?? null}
        onCancel={() => setEvolutionJobModalOpen(false)}
        onConfirm={handleSaveEvolutionJobNumber}
        loading={updateOrderMutation.isPending}
      />


<ResendPaymentLinkModal
        open={resendPaymentModalOpen}
        onClose={() => setResendPaymentModalOpen(false)}
        onConfirm={handleResendPaymentLink}
        isLoading={isSendingPayment}
      />
    </div>
  );
};

export default OrderDetailPage;
